import React from "react";
import type {
    CarModelLabelType,
    CommonFinanceInfoType,
    FinanceOptionType,
    PriceType,
} from "../../../../../../../common-deprecated/types/CommonTypes";
import { useBnbSelector } from "../../../../redux/store";
import CashPrice from "./CashPrice";
import MonthlyPrice from "./MonthlyPrice";
import {
    getBnbMonthlyRateFormat,
    getBnbPriceConfig,
    shouldNotRenderAemPrice,
} from "../../../../utils/settings/bnbFinanceSettingUtils";
import { renderMonthlyRateWithPrice } from "../../../../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";
import { getCashPriceDisclaimerLabel } from "../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { isIsrael } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import CashPriceIsrael from "./CashPriceIsrael";

type PriceBasePropsType = {
    price: PriceType;
    // If no financeOption is supplied the currently active finance option will be used.
    financeOption?: FinanceOptionType;
    carInternalCode?: string;
    isLarge?: boolean;
    showLoader?: boolean; // shows loading string when loading
    hideWhenLoading?: boolean; // do not show anything when loading, overrides showLoader
    forceMonthlyDisclaimer?: boolean; // Passed into MonthlyRateDisclaimer to force ignore the flexibility matrix configuration
    forceHideMonthlyDisclaimer?: boolean; // Used when the MonthlyRateDisclaimer needs to be shown in different location, see FinanceService, takes precedence over forceMonthlyDisclaimer
    cashPriceDisclaimer?: CarModelLabelType[] | string | null; // Can be used as CarModelLabelType (finance calls) or string (i18n label), currently used as VAT disclaimer for Israel & genericPriceDisclaimers for Lexus.
};

// Price component for the build either needs to receive financeInfo or a finance item id.
type PriceFinanceItemType = PriceBasePropsType & {
    financeItemId: string;
};

type PriceFinanceInfoType = PriceBasePropsType & {
    financeInfo?: CommonFinanceInfoType;
};

const priceContainsItemId = (props: PriceFinanceItemType | PriceFinanceInfoType): props is PriceFinanceItemType =>
    props.hasOwnProperty("financeItemId"); // eslint-disable-line no-prototype-builtins

const Price = (props: PriceFinanceItemType | PriceFinanceInfoType): JSX.Element | null => {
    const { price, carInternalCode, isLarge, hideWhenLoading, cashPriceDisclaimer } = props;
    const { forceMonthlyDisclaimer, forceHideMonthlyDisclaimer, showLoader } = props;
    const financeItemId = priceContainsItemId(props) ? props.financeItemId : "";

    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const modelCode = useBnbSelector((state) => carInternalCode || state.car?.model?.internalCode || "");
    const financeOption = useBnbSelector((state) => props.financeOption || state.utils.financeOption);
    const tradeInVehiclePrice = useBnbSelector((state) => state.tradeInAemExternalData.data?.vehicleValue);
    const financeInfo = useBnbSelector((state) =>
        priceContainsItemId(props) ? state.finance.info : props.financeInfo,
    );
    const monthlyLoading = useBnbSelector((state) => state.finance.fetching);
    const cashLoading = useBnbSelector(
        (state) => state.externalPrice.fetchingFunctions || state.externalPrice.fetchingValue,
    );
    const step = useBnbSelector((state) => state.navigation.step);

    // When toggleConfigurablesMonthlyRate is false the cash price should always be shown except for total prices.
    // See OR-396.
    const forceCashPrice =
        !bnbSettings.toggleConfigurablesMonthlyRate &&
        financeItemId !== "total" &&
        !(props as PriceFinanceInfoType).financeInfo;

    const ignorePriceValue = showLoader || (forceCashPrice && financeOption === "monthly");

    // If a financeItemId item id is supplied retrieve the monthly rate for that item from the store.
    const monthly: number = useBnbSelector((state) => {
        if (financeItemId) {
            if (financeItemId === "total") {
                return Number(state.finance.total);
            } else {
                return typeof state.finance.itemRates[financeItemId] !== "undefined"
                    ? Number(state.finance.itemRates[financeItemId])
                    : 0;
            }
        } else {
            return price.monthly;
        }
    });

    // Create a new object to prevent mutating the original price with the parsed monthly rate.
    const renderedPrice = { ...price, monthly };
    if (
        // Hide when the price should not be rendered
        shouldNotRenderAemPrice(
            commonSettings,
            bnbSettings,
            renderedPrice,
            financeOption,
            step,
            modelCode,
            ignorePriceValue,
        ) ||
        // Hide when loading
        (hideWhenLoading && (cashLoading || monthlyLoading))
    ) {
        return null;
    }

    // If a trade-in vehicle price is present remove it from the cash total price.
    if (financeItemId === "total" && tradeInVehiclePrice && renderedPrice.discount > tradeInVehiclePrice) {
        renderedPrice.discount -= tradeInVehiclePrice;
    }

    const cashProps = {
        cultureName: commonSettings.culture.name,
        price: renderedPrice,
        priceConfig: getBnbPriceConfig(commonSettings, modelCode, step),
        isLarge,
        showLoader: showLoader && cashLoading,
        cashPriceDisclaimer: cashPriceDisclaimer
            ? typeof cashPriceDisclaimer === "string"
                ? cashPriceDisclaimer
                : getCashPriceDisclaimerLabel(cashPriceDisclaimer)
            : null,
    };

    // show cash price together with monthly rate, but do not yet show cash price when monthly rate is loading
    const renderMonthlyWithCash =
        renderMonthlyRateWithPrice(commonSettings) && (!showLoader || (showLoader && !monthlyLoading));

    return (
        <>
            {(financeOption === "cash" || forceCashPrice) && (
                <>
                    {isIsrael(cashProps.cultureName) ? (
                        <CashPriceIsrael {...cashProps} />
                    ) : (
                        <CashPrice {...cashProps} />
                    )}
                </>
            )}
            {financeOption === "monthly" && !forceCashPrice && financeInfo && (
                <>
                    <MonthlyPrice
                        price={renderedPrice}
                        priceFormat={getBnbMonthlyRateFormat(commonSettings, modelCode)}
                        financeInfo={financeInfo}
                        forceMonthlyDisclaimer={forceMonthlyDisclaimer}
                        forceHideDisclaimer={forceHideMonthlyDisclaimer}
                        showLoader={showLoader && monthlyLoading}
                    />
                </>
            )}
            {financeOption === "monthly" && renderMonthlyWithCash && (
                <>
                    {isIsrael(cashProps.cultureName) ? (
                        <CashPriceIsrael {...cashProps} small />
                    ) : (
                        <CashPrice {...cashProps} small />
                    )}
                </>
            )}
        </>
    );
};

export default Price;
